import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getActorDisplayName } from '../../utils/roleUtils';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import {
    createCustomInvitation,
    getAllActors,
} from '../../services/apiService';
import { EMAIL_REGEX } from '../../utils/emailUtils';

interface Actor {
    _id: string;
    role: string;
}

interface AddGuestDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onAdded: () => void;
}

export default function AddGuestDialog({
    open,
    onOpenChange,
    onAdded,
}: AddGuestDialogProps) {
    const { toast } = useToast();
    const { t } = useTranslation();
    const [actors, setActors] = useState<Actor[]>([]);
    const [email, setEmail] = useState('');
    const [actorId, setActorId] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setEmail('');
        setActorId('');
        setExpiryDate('');

        const loadActors = async () => {
            try {
                const data = await getAllActors();
                setActors(data);
            } catch {
                toast({
                    title: t('facilities.addGuestDialog.toasts.loadRolesError'),
                    variant: 'destructive',
                });
            }
        };
        loadActors();
    }, [open, toast, t]);

    const handleSubmit = async () => {
        if (!EMAIL_REGEX.test(email.trim())) {
            toast({
                title: t('facilities.addGuestDialog.toasts.invalidEmail'),
                description: t('facilities.addGuestDialog.toasts.invalidEmailDesc'),
                variant: 'destructive',
            });
            return;
        }
        if (!expiryDate) {
            toast({
                title: t('facilities.addGuestDialog.toasts.endDateRequired'),
                description: t('facilities.addGuestDialog.toasts.endDateRequiredDesc'),
                variant: 'destructive',
            });
            return;
        }

        setLoading(true);
        try {
            await createCustomInvitation({
                email: email.trim(),
                activationDate: new Date().toLocaleDateString('en-US'),
                expiryDate,
                ...(actorId && { actor: actorId }),
            });
            toast({ title: t('facilities.addGuestDialog.toasts.guestSent') });
            onOpenChange(false);
            onAdded();
        } catch {
            toast({
                title: t('facilities.addGuestDialog.toasts.inviteError'),
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('facilities.addGuestDialog.title')}</DialogTitle>
                    <DialogDescription>
                        {t('facilities.addGuestDialog.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="guest-email">{t('facilities.addGuestDialog.email')}</Label>
                        <Input
                            id="guest-email"
                            type="email"
                            placeholder={t('facilities.addGuestDialog.emailPlaceholder')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="guest-actor">{t('facilities.addGuestDialog.roleOptional')}</Label>
                        <Select value={actorId} onValueChange={setActorId}>
                            <SelectTrigger id="guest-actor">
                                <SelectValue placeholder={t('facilities.addGuestDialog.chooseRole')} />
                            </SelectTrigger>
                            <SelectContent>
                                {actors.map((a) => (
                                    <SelectItem key={a._id} value={a._id}>
                                        {getActorDisplayName(a)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>{t('facilities.addGuestDialog.startDate')}</Label>
                            <Input
                                type="text"
                                disabled
                                value={new Date().toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                })}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="guest-expiry">{t('facilities.addGuestDialog.endDate')}</Label>
                            <Input
                                id="guest-expiry"
                                type="date"
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        {t('facilities.addGuestDialog.cancel')}
                    </Button>
                    <Button onClick={handleSubmit} disabled={loading}>
                        {loading ? t('facilities.addGuestDialog.sending') : t('facilities.addGuestDialog.sendInvite')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
